import { DataLoader } from '../../../core/types/data-loader';
import {
  StateStore,
  PartialStateStore
} from '../../../core/types/application-state';
import {
  isEmpty,
  withDefault,
  deleteCookie,
  getCookie
} from '../../../core/util';
import {
  appendMailingAddressIfDifferent,
  populateResidentialAddress
} from '../../shared/loaders';
import { postAddressValidation } from '../../shared/services/address-validation-service';
import {
  PhoneNumberType,
  parsePhone,
  getOriginalResidentialAddress,
  getCorrectedResidentialAddress,
  getOriginalMailingAddress,
  getCorrectedMailingAddress,
  consumerTypes,
  chargeTypes,
  accountTypes,
  extractPhoneNumberwithType,
  getCustomerMailingAddress
} from '../../shared/utils';
import { houseHoldSummitData } from '../../shared/summit.utils';
import { formatDate } from '../../shared/dateUtils';
import {
  createNewJourneyProfile,
  deleteJourneyProfile,
  getExistingApplications
} from '../../shared/services/profile-service';
import { registerUserWithTempPassword } from '../../shared/services/user-service';
import { getCustomerHouseholdDataByTaxId } from '../../shared/services/household-service';
import {
  convertAppDataToProfileData,
  removeExtraAddressFields
} from '../../shared/services/util';
import { encode_deviceprint as encodeDeviceprint } from '../../../rsa-fingerprint';
import {
  getApplicationType,
  getCusip,
  convertFONASUCustomerCitizenshipType,
  convertFONASUCustomerPhoneNumbers,
  getRolloverCustodianInfo,
  isCurrentAndPriorYearAmount,
  isRPSOCRAccount,
  isOCRExistingAgreementsEditable
} from './utils';
import {
  dispatchFONASUPushToWebCustomEvent,
  dispatchProfileErrorEvent
} from '../../../analytics/analytics-helper';

export const loadFONASUCustomerData = (customerData): any => {
  const customer = {
    name: {
      firstName: withDefault(customerData?.firstName, ''),
      middleName: withDefault(customerData?.middleName, ''),
      lastName: withDefault(customerData?.lastName, '')
    },
    primaryEmailAddress: withDefault(customerData?.primaryEmailAddress, ''),
    dob: withDefault(customerData?.dateOfBirthString, ''),
    taxID: withDefault(customerData?.taxID, ''),
    residentialAddress: withDefault(customerData?.primaryAddress, {
      street1: '',
      city: '',
      state: '',
      zip: ''
    }),
    phones: convertFONASUCustomerPhoneNumbers(customerData?.phoneNumbers),
    mailingAddress: withDefault(
      getCustomerMailingAddress(customerData?.addresses),
      {}
    ),
    citizenship: convertFONASUCustomerCitizenshipType(
      customerData?.citizenshipType
    ),
    taxIDType: 'SSN'
  };

  return removeExtraAddressFields(customer);
};

export const getFilteredExistingApps = (
  existingApps,
  toIRAAccountType,
  chargeType
): any => {
  if (chargeType === chargeTypes.TRANSFER) {
    return existingApps.filter(
      item =>
        item.consumerName === consumerTypes.FONASU &&
        item.toAccountType === toIRAAccountType
    );
  }

  return existingApps.filter(
    item => item.consumerName === consumerTypes.FONASU
  );
};

const checkForExistingApplications = async (
  primaryEmailAddress,
  accountType,
  chargeType,
  toIRAAccountType
): Promise<any> => {
  if (!(accountType?.includes('OMNI') || accountType?.includes('TRAC'))) {
    const existingApps = await getExistingApplications(
      primaryEmailAddress,
      accountType
    );

    const filteredExistingApps =
      Array.isArray(existingApps) &&
      getFilteredExistingApps(existingApps, toIRAAccountType, chargeType);

    if (filteredExistingApps?.length > 0) {
      return filteredExistingApps.map(values => {
        const { applicationID } = values;
        return applicationID;
      });
    }
  }
  return '';
};

export const getTotalInvestmentAmount = (
  currentYearAmount,
  priorYearAmount,
  amount
): any => {
  if (isCurrentAndPriorYearAmount(currentYearAmount, priorYearAmount)) {
    return (
      parseInt(priorYearAmount?.toString().replace(/[^0-9]/g, ''), 10) +
      parseInt(currentYearAmount?.toString().replace(/[^0-9]/g, ''), 10)
    );
  }

  if (currentYearAmount || priorYearAmount) {
    return withDefault(
      parseInt(currentYearAmount?.toString().replace(/[^0-9]/g, ''), 10),
      parseInt(priorYearAmount?.toString().replace(/[^0-9]/g, ''), 10)
    );
  }

  return parseInt(amount?.toString().replace(/[^0-9]/g, ''), 10);
};

const getUpdatedCustomerInfo = async (
  residentialAddressValidation,
  mailingAddressValidation,
  inputs,
  store,
  chosen
): Promise<any> => {
  const {
    currentYearAmount,
    priorYearAmount,
    amount,
    citizenship,
    dob,
    firstName,
    lastName,
    middleName,
    primaryEmailAddress,
    ssn,
    mailingAddressDifferent,
    phone1,
    phone2,
    phone3,
    successorAccountHolder,
    successorFirstName,
    successorMiddleName,
    successorLastName,
    successorDob
  } = inputs;

  const { app, loadedData, accountType } = store;
  const { customer } = app;
  const { associateLoginID } = chosen;

  const taxID = ssn?.replace(/[^*0-9]/g, '');
  const mobilePhone = extractPhoneNumberwithType(customer?.phones, 'MOBILE');
  const dayTimePhone = extractPhoneNumberwithType(customer?.phones, 'DAYTIME');
  const eveningPhone = extractPhoneNumberwithType(customer?.phones, 'EVENING');

  const phones = [
    { phone: withDefault(phone1, mobilePhone), type: PhoneNumberType.CELLULAR },
    {
      phone: withDefault(phone2, dayTimePhone),
      type: PhoneNumberType.DAYTIME
    },
    { phone: withDefault(phone3, eveningPhone), type: PhoneNumberType.EVENING }
  ]
    .filter(({ phone }) => phone)
    .map(({ phone, type }) => parsePhone(phone, type));

  const { toIRAAccountType, chargeType } = app;

  const appID = await checkForExistingApplications(
    withDefault(primaryEmailAddress, customer?.primaryEmailAddress),
    accountType,
    chargeType,
    toIRAAccountType
  );

  let appError = '';

  const isOCRAccountType = isRPSOCRAccount(accountType);

  if (appID && !isOCRAccountType) {
    appError = `A front office application of type ${accountType} ${getApplicationType(
      toIRAAccountType
    )} already exists. 
    Click continue to override the previous app, or close the browser and have the client log in 
    to finish the application in Web NASU`;
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  const res = {
    residentialAddressValidation,
    ...(mailingAddressDifferent && { mailingAddressValidation }),
    mailingAddressDifferent: Boolean(mailingAddressDifferent),
    app: {
      customer: {
        name: {
          firstName: withDefault(firstName, customer?.name?.firstName),
          lastName: withDefault(lastName, customer?.name?.lastName),
          middleName: withDefault(middleName, customer?.name?.middleName)
        },
        dob: formatDate(withDefault(dob, customer?.dob), 'MM/DD/YYYY', ''),
        citizenship: withDefault(citizenship, customer?.citizenship),
        taxID: withDefault(taxID, customer?.taxID),
        taxIDType: 'SSN',
        phones,
        primaryEmailAddress: withDefault(
          primaryEmailAddress,
          customer?.primaryEmailAddress
        )
      }
    },

    amount: withDefault(
      getTotalInvestmentAmount(currentYearAmount, priorYearAmount, amount),
      0
    ),
    loadedData: {
      ...loadedData,
      error: isOCRAccountType ? loadedData.error : appError,
      appID,
      closedTickers: [],
      priorYearAmount: withDefault(
        parseInt(priorYearAmount?.toString().replace(/[^0-9]/g, ''), 10),
        ''
      ),
      currentYearAmount: withDefault(
        parseInt(currentYearAmount?.toString().replace(/[^0-9]/g, ''), 10),
        ''
      ),
      usernameTaken: false,
      usernameCreationFailed: false,
      tempPasswordCreationFailed: false,
      scrollableModal: false,
      associateLoginID,
      successorAccountHolder,
      successorInfo: {
        successorFirstName,
        successorMiddleName,
        successorLastName,
        successorDOB: formatDate(successorDob, 'MM/DD/YYYY', '')
      }
    }
  };
  return res;
};
/* eslint-enable */

const commitFONASUAddressValidation = async (
  inputs,
  store,
  chosen
): Promise<StateStore> => {
  const {
    app: { customer }
  } = store;
  const { mailingAddressDifferent } = inputs;
  const { residentialAddress, mailingAddress } = customer;

  const residentialStreet1 = withDefault(
    inputs?.residentialStreet1,
    residentialAddress?.street1
  );
  const residentialStreet2 = withDefault(inputs.residentialStreet2, '');
  const residentialZip = withDefault(
    inputs?.residentialZip,
    residentialAddress?.zip
  );
  const residentialCity = withDefault(
    inputs?.residentialCity,
    residentialAddress?.city
  );
  const residentialState = withDefault(
    inputs?.residentialState,
    residentialAddress?.state
  );

  const residentialAddressInputs = {
    city: residentialCity,
    country: 'United States',
    state: residentialState,
    street1: residentialStreet1,
    street2: residentialStreet2,
    street3: '',
    usaddress: true,
    zip: residentialZip
  };

  let mailingAddressInputs = {};
  if (mailingAddressDifferent) {
    const mailingStreet1 = withDefault(
      inputs?.mailingStreet1,
      mailingAddress?.street1
    );
    const mailingStreet2 = withDefault(inputs?.mailingStreet2, '');
    const mailingZip = withDefault(inputs?.mailingZip, mailingAddress?.zip);
    const mailingCity = withDefault(inputs?.mailingCity, mailingAddress?.city);
    const mailingState = withDefault(
      inputs?.mailingState,
      mailingAddress?.state
    );

    mailingAddressInputs = {
      city: mailingCity,
      country: 'United States',
      state: mailingState,
      street1: mailingStreet1,
      street2: mailingStreet2,
      street3: '',
      usaddress: true,
      zip: mailingZip,
      zip4: ''
    };
  }

  let addresses = [residentialAddressInputs];
  addresses = appendMailingAddressIfDifferent(
    addresses,
    mailingAddressDifferent,
    mailingAddressInputs
  );

  let residentialAddressValidation;
  let mailingAddressValidation;
  try {
    const [
      residentialAddressValid,
      mailingAddressValid
    ] = await postAddressValidation(addresses);

    residentialAddressValidation =
      residentialAddressValid || residentialAddressInputs;
    mailingAddressValidation = mailingAddressValid || mailingAddressInputs;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
    residentialAddressValidation = residentialAddressInputs;
    mailingAddressValidation = mailingAddressInputs;
  }
  const res = await getUpdatedCustomerInfo(
    residentialAddressValidation,
    mailingAddressValidation,
    inputs,
    store,
    chosen
  );

  return res;
};

const personalInfoMailingAddress = (
  mailingAddressDifferent,
  useMailingAddress,
  originalMailingAddress,
  correctedMailingAddress,
  residentialAddress
): any => {
  if (mailingAddressDifferent) {
    if (useMailingAddress === 'useOriginalMailingAddress') {
      const { street1, street2, city, state, zip } = originalMailingAddress;
      return {
        street1,
        street2,
        city,
        state,
        zip,
        corrected: false
      };
    }
    const { street1, street2, city, state, zip } = correctedMailingAddress;
    return {
      street1,
      street2,
      city,
      state,
      zip,
      corrected: true
    };
  }
  return residentialAddress;
};

export const getNextUnlockedSection = (store): any => {
  const { accountType, loadedData } = store;

  const isOCRExistingAgreeEdit = isOCRExistingAgreementsEditable(
    loadedData,
    accountType
  );

  if (accountType === accountTypes.JOINT) {
    return { jointOwnerEditMode: true };
  }
  if (accountType === accountTypes.UGMAUTMA) {
    return { minorEditMode: true };
  }
  if (isOCRExistingAgreeEdit) {
    return {
      existingAgreementsEditMode: withDefault(
        loadedData.existingAgreements?.length > 0,
        false
      )
    };
  }

  return { portfolioEditMode: true };
};

/* eslint-disable */
export const commitFONASUPersonalInformation = async (
  inputs,
  store,
  chosen
): Promise<PartialStateStore> => {
  if (chosen?.section === 'addressValidation') {
    return commitFONASUAddressValidation(inputs, store, chosen);
  }

  const {
    residentialAddressValidation,
    mailingAddressValidation = {},
    mailingAddressDifferent,
    loadedData
  } = store;

  const originalResidentialAddress = getOriginalResidentialAddress(
    residentialAddressValidation
  );
  const correctedResidentialAddress = getCorrectedResidentialAddress(
    residentialAddressValidation
  );
  const originalMailingAddress = getOriginalMailingAddress(
    mailingAddressValidation
  );
  const correctedMailingAddress = getCorrectedMailingAddress(
    mailingAddressValidation
  );

  const { useResidentialAddress, useMailingAddress } = inputs;

  const residentialAddress = populateResidentialAddress(
    useResidentialAddress,
    originalResidentialAddress,
    correctedResidentialAddress
  );

  const mailingAddress = personalInfoMailingAddress(
    mailingAddressDifferent,
    useMailingAddress,
    originalMailingAddress,
    correctedMailingAddress,
    residentialAddress
  );

  const unlockNextSection = !loadedData?.error && getNextUnlockedSection(store);

  const res = {
    app: {
      customer: {
        residentialAddress,
        mailingAddress
      }
    },
    loadedData: {
      infoValid: true,
      ...unlockNextSection,
      customerInfoEditMode: false,
      scrollableModal: false,
      usernameTaken: false,
      usernameCreationFailed: false,
      tempPasswordCreationFailed: false
    }
  };

  return res;
};
/* eslint-enable */

export const commitFONASUNewInvestments = async (
  chosen
): Promise<PartialStateStore> => {
  const {
    funds,
    fundingType,
    appxRolloverAmt,
    rolloverAmountOption,
    rewardsAvailable
  } = chosen;

  let closedTickers = [];

  const newInvestments = funds.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage
    }) => ({
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  const totalAmount = funds.reduce((accumulator, { amount }) => {
    return accumulator + amount;
  }, 0);

  if (!rewardsAvailable.closedFunds) {
    closedTickers = funds
      .filter(({ openToNewInvestors }) => !openToNewInvestors)
      .map(f => f.ticker);
  }

  return {
    app: {
      newInvestments,
      investmentMethod: 'BYOP'
    },
    amount: totalAmount,
    loadedData: {
      portfolioValid: true,
      portfolioEditMode: false,
      fundingEditMode: true,
      beneInfoEditMode: false,
      usernameTaken: false,
      usernameCreationFailed: false,
      tempPasswordCreationFailed: false,
      closedTickers,
      fundingType,
      appxRolloverAmt,
      rolloverAmountOption
    }
  };
};

export const handleCredentialsValidation = (err): any => {
  const usernameTaken =
    Boolean(err.toString().match(/serviceErrorCode/)) &&
    Boolean(err.toString().match(/100020/));
  if (usernameTaken) {
    return {
      usernameTaken,
      usernameCreationFailed: false,
      tempPasswordCreationFailed: false
    };
  }
  return {
    usernameTaken: false,
    usernameCreationFailed: true,
    tempPasswordCreationFailed: false
  };
};

export const commitFONASUCredentials = async (
  store,
  inputs
): Promise<PartialStateStore> => {
  const { username: loginID } = inputs;
  const {
    loadedData: { brkAccType }
  } = store;

  // get the bpId cookie value
  const bpIdValue = getCookie('bpId');

  try {
    const { resultType, tempPassword } = await registerUserWithTempPassword({
      /*  read required customer data from store for FONASU MF flows and 
          for FONASU BRK Flows read if from loadedData
      */
      firstName: withDefault(
        store?.app?.customer?.name?.firstName,
        store?.loadedData?.customerFirstName
      ),
      lastName: withDefault(
        store?.app?.customer?.name?.lastName,
        store?.loadedData?.customerLastName
      ),
      emailAddress: withDefault(
        store?.app?.customer?.primaryEmailAddress,
        store?.loadedData?.customerPrimaryEmail
      ),
      loginID,
      bpid: bpIdValue,
      userAgent: navigator.userAgent,
      rsaDeviceToken: encodeDeviceprint()
    });

    // temp password creation failure flag only to be set for MF FONASU flows
    if (!brkAccType && (resultType === 'FAILURE' || tempPassword === '')) {
      return {
        loadedData: {
          loginID,
          usernameTaken: false,
          usernameCreationFailed: false,
          tempPasswordCreationFailed: true
        }
      };
    }

    return {
      loadedData: {
        tempPassword,
        loginID,
        usernameTaken: false,
        tempPasswordCreationFailed: false,
        usernameCreationFailed: false
      }
    };
  } catch (err) {
    const errorResp = handleCredentialsValidation(err);
    return {
      loadedData: {
        ...errorResp,
        loginID
      }
    };
  }
};

export const commitFONASUCustodian = async (
  chosen,
  inputs
): Promise<PartialStateStore> => {
  const {
    custodian,
    custodianFunds,
    fundsInBrokerage,
    preliquidated,
    cusips
  } = chosen;

  const {
    branchAddressRequired,
    custodianID,
    custodianName,
    toraParticipant,
    customerServicePhone,
    regularAddressLine1,
    regularAddressLine2,
    regularAddressCity,
    regularAddressState,
    regularAddressZip
  } = custodian;

  const {
    funds: accounts,
    custodianBranchAddressStreet1,
    custodianBranchAddressStreet2,
    custodianBranchZip,
    custodianBranchCity,
    custodianBranchState
  } = inputs;

  const custodianBranchAddress = {
    street1: custodianBranchAddressStreet1?.trim(),
    street2: custodianBranchAddressStreet2?.trim(),
    city: custodianBranchCity?.trim(),
    state: custodianBranchState?.trim(),
    zip: custodianBranchZip?.trim()
  };

  const custodianResidentialAddress = {
    street1: regularAddressLine1?.trim(),
    street2: regularAddressLine2?.trim(),
    city: regularAddressCity?.trim(),
    state: regularAddressState?.trim(),
    zip: regularAddressZip?.trim()
  };

  const res = {
    app: {
      custodian: {
        custodianID,
        custodianName,
        custodianAddress: branchAddressRequired
          ? custodianBranchAddress
          : custodianResidentialAddress,
        phone: customerServicePhone
      },
      existingAccounts: [],
      externalAccounts: accounts.map(
        (
          { accountName, accountNumber, transferAmount, transferBalance },
          idx
        ) => ({
          accountName,
          accountNumber,
          accountType: 'MUTUAL_FUND',
          estimatedTransferAmount: parseFloat(transferAmount),
          liquidationOption: transferBalance,
          cusip: withDefault(getCusip(cusips, idx), '')
        })
      )
    },
    fundsInBrokerage: Boolean(fundsInBrokerage),
    preliquidated: Boolean(preliquidated),
    toraParticipant: Boolean(toraParticipant),
    custodianCustomerServicePhone: customerServicePhone,
    loadedData: {
      custodianFunds,
      cusips,
      fundingValid: true,
      fundingEditMode: false
    }
  };
  return res;
};

export const commitFONASURolloverType = async (
  chosen,
  inputs
): Promise<PartialStateStore> => {
  const { custodian } = chosen;

  const { formerEmployer, rolloverType } = inputs;

  const res = {
    app: {
      custodian: getRolloverCustodianInfo(rolloverType, custodian)
    },
    loadedData: {
      closedTickers: [],
      fundingValid: true,
      fundingEditMode: false,
      rolloverType,
      formerEmployer: withDefault(formerEmployer, '')
    }
  };
  return res;
};

export const commitFONASUJointOwners = async (
  inputs,
  store
): Promise<PartialStateStore> => {
  const { people } = inputs;
  const {
    app: {
      customer: { residentialAddress: customerAddress }
    }
  } = store;

  const res = {
    additionalCustomers: people?.map(person => ({
      customerType: 'JOINT_TENANT',
      name: {
        firstName: person.jointOwnerFirstName,
        middleName: person.jointOwnerMiddleName,
        lastName: person.jointOwnerLastName
      },
      dob: formatDate(person.jointOwnerDob, 'MM/DD/YYYY', ''),
      taxID: person.jointOwnerSsn.replace(/[^*0-9]/g, ''),
      citizenship: person.jointOwnerCitizenship,
      residentialAddress: person.jointOwnerSameAddress
        ? customerAddress
        : {
            street1: person.jointOwnerResidentialStreet1,
            street2: person.jointOwnerResidentialStreet2,
            state: person.jointOwnerResidentialState,
            zip: person.jointOwnerResidentialZip,
            city: person.jointOwnerResidentialCity
          },
      sameAddress: !!person.jointOwnerSameAddress
    })),
    loadedData: {
      jointOwnerValid: true,
      jointOwnerEditMode: false,
      portfolioEditMode: true
    }
  };

  return res;
};

const getGoverningState = (
  minorSameAddress,
  customerState,
  minorState,
  governingState
): string => {
  if (minorSameAddress) return customerState;

  if (customerState !== minorState) return governingState;
  return customerState;
};

export const commitFONASUMinorCustomer = async (
  inputs,
  store
): Promise<PartialStateStore> => {
  const {
    minorFirstName,
    minorMiddleName,
    minorLastName,
    minorDob,
    minorSsn,
    minorCitizenship,
    minorSameAddress,
    minorResidentialStreet1,
    minorResidentialStreet2,
    minorResidentialCity,
    minorResidentialState,
    minorResidentialZip,
    minorGoverningState
  } = inputs;

  const {
    app: {
      customer: { residentialAddress: customerAddress }
    }
  } = store;

  const minorHHdata = await getCustomerHouseholdDataByTaxId({
    firstName: minorFirstName,
    lastName: minorLastName,
    dateOfBirth: minorDob,
    taxId: minorSsn.replace(/[^*0-9]/g, '')
  });

  const summitData = houseHoldSummitData(minorHHdata);

  const res = {
    app: {
      governingState: getGoverningState(
        minorSameAddress,
        customerAddress.state,
        minorResidentialState,
        minorGoverningState
      )
    },
    additionalCustomers: [
      {
        customerType: 'MINOR',
        name: {
          firstName: minorFirstName,
          middleName: minorMiddleName,
          lastName: minorLastName
        },
        dob: formatDate(minorDob, 'MM/DD/YYYY', ''),
        taxID: minorSsn.replace(/[^*0-9]/g, ''),
        citizenship: minorCitizenship,
        residentialAddress: minorSameAddress
          ? customerAddress
          : {
              street1: minorResidentialStreet1,
              street2: minorResidentialStreet2,
              state: minorResidentialState,
              zip: minorResidentialZip,
              city: minorResidentialCity
            },
        sameAddress: !!minorSameAddress
      }
    ],
    loadedData: {
      minorInfoValid: true,
      minorEditMode: false,
      portfolioEditMode: true
    },
    summitData
  };
  return res;
};

const commitFONASUMFIRAFunding = (section, chosen, inputs): Promise<any> => {
  if (section === 'externalAccount') {
    return commitFONASUCustodian(chosen, inputs);
  }
  return commitFONASURolloverType(chosen, inputs);
};

const commitFONASUTaxAdditionalCustInfo = (
  section,
  inputs,
  store
): Promise<PartialStateStore> => {
  if (section === 'jointOwners') {
    return commitFONASUJointOwners(inputs, store);
  }
  return commitFONASUMinorCustomer(inputs, store);
};

const getSectionsForExternalOrRollover = (section): any => {
  return section === 'externalAccount' || section === 'rolloverType';
};

const getSectionsForAdditionalCustInfo = (section): any => {
  return section === 'jointOwners' || section === 'minorCustomer';
};

export const commitFONASUSection = async (
  chosen,
  inputs,
  store
): Promise<any> => {
  const { section } = chosen;

  const mfIraFundingSectionType = getSectionsForExternalOrRollover(section);
  const additionalCustInfoSectionType = getSectionsForAdditionalCustInfo(
    section
  );

  if (mfIraFundingSectionType) {
    return commitFONASUMFIRAFunding(section, chosen, inputs);
  }
  if (additionalCustInfoSectionType) {
    return commitFONASUTaxAdditionalCustInfo(section, inputs, store);
  }
  return commitFONASUNewInvestments(chosen);
};

/* eslint-disable */
export const createFONASUApplication: DataLoader = async ({
  store,
  inputs,
  chosen
}): Promise<PartialStateStore> => {
  let error = '';

  const { app, accountType, loadedData } = store;

  if (chosen === 'deleteApplication') {
    await deleteJourneyProfile(store.loadedData.appID[0]);
    const unlockNextSection = getNextUnlockedSection(store);
    return {
      loadedData: { ...loadedData, ...unlockNextSection, error }
    };
  }

  if (isEmpty(chosen) || chosen?.section === 'addressValidation') {
    return commitFONASUPersonalInformation(inputs, store, chosen);
  }

  if (chosen.section) {
    return commitFONASUSection(chosen, inputs, store);
  }

  if (chosen === 'username') {
    return commitFONASUCredentials(store, inputs);
  }

  if (chosen?.action === 'publish') {
    try {
      const profileApplicationID = (
        await createNewJourneyProfile(convertAppDataToProfileData(store))
      )?.applicationID;
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });

      // send custom event tracking to analytics on FONASU application submission
      dispatchFONASUPushToWebCustomEvent();

      //delete the bpId cookie once the application/profile is published
      deleteCookie('bpId');
      return {
        ...store,
        app: { ...app, profileApplicationID }
      };
    } catch (error) {
      // send error tracking to analytics
      dispatchProfileErrorEvent(
        'Failed to publish push to web to profile service.'
      );
    }
  }

  if (chosen === 'unlockMyInfoSection') {
    return {
      loadedData: {
        customerInfoEditMode: true,
        beneInfoEditMode: false,
        portfolioEditMode: false,
        jointOwnerEditMode: false,
        minorEditMode: false,
        fundingEditMode: false
      }
    };
  }
  if (chosen === 'unlockPortfolioSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        beneInfoEditMode: false,
        portfolioEditMode: true,
        jointOwnerEditMode: false,
        minorEditMode: false,
        fundingEditMode: false
      }
    };
  }
  if (chosen === 'unlockJointOwnerSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        beneInfoEditMode: false,
        portfolioEditMode: false,
        jointOwnerEditMode: true,
        minorEditMode: false,
        fundingEditMode: false
      }
    };
  }
  if (chosen === 'unlockMinorSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        beneInfoEditMode: false,
        portfolioEditMode: false,
        jointOwnerEditMode: false,
        minorEditMode: true,
        fundingEditMode: false
      }
    };
  }
  if (chosen === 'unlockRolloverFundingSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        beneInfoEditMode: false,
        portfolioEditMode: false,
        jointOwnerEditMode: false,
        minorEditMode: false,
        fundingEditMode: true
      }
    };
  }
  return { app };
};
